import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import { ChatAiConfig } from '../interfaces/chat-ai-config';
import { MAIS_API_BASE_URL } from '../tokens/mais-api-base-url.token';

export interface MaisAttachment {
  name: string;
  mime: string;
  contentString: string;
}

export interface MaisSource {
  title: string;
  chunk: string;
  // Present on the real server's wire payload alongside title/chunk (confirmed against a live
  // capture) but not part of the documented schema - optional so older/other server responses
  // that omit them still type-check.
  text?: string;
  score?: number;
  chunkSource?: string;
}

// Present on the real wire payload (confirmed against multiple live captures) but not part of
// the documented schema. `state` has only ever been observed as 'output-available' - even on
// the very first chunk of a turn, before any sources or answer text exist yet - so there is no
// confirmed signal here for "this specific call is still running" beyond the surrounding
// turn's own streaming/sources/textResponse state.
export interface MaisToolCall {
  toolCallId: string;
  toolName: string;
  input?: Record<string, unknown>;
  state?: string;
}

// Present on the real wire payload's closing chunk (and on a GET .../chats history item) but not
// part of the documented schema. app_overhead_ms has only ever been observed null - the server
// doesn't report end-to-end latency today, so callers time the round trip client-side instead.
export interface MaisMetrics {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  app_overhead_ms?: number | null;
}

export interface MaisStreamChunk {
  id: string;
  type: string;
  textResponse?: string;
  sources?: MaisSource[];
  toolCalls?: MaisToolCall[];
  close?: boolean;
  error?: string;
  // Populated on the final (close:true) event of a stream-chat turn - the actual persisted
  // chats-table row id, same value GET .../chats returns as `chatId`. Not in the documented
  // schema; observed directly against a live server.
  chatId?: number;
  metrics?: MaisMetrics;
}

// Maps 1:1 to MAIS's documented, public /v1 REST API (bearer-JWT). Deliberately has zero
// dependency on the CloudLink SDK - this is the only place aiMode talks to the network.
@Injectable({ providedIn: 'root' })
export class ChatMaisService {
  readonly messagesChanged$ = new Subject<MaisStreamChunk>();

  constructor(
    private http: HttpClient,
    @Inject(MAIS_API_BASE_URL) private defaultBaseUrl: string
  ) {}

  createThread(config: ChatAiConfig): Observable<any> {
    return this.http.post(`${this.workspaceUrl(config)}/thread/new`, {}, { headers: this.authHeaders(config) });
  }

  // Confirmed against the live server's actual model code (server/models/workspaceChats.js) and
  // by reproducing it end-to-end: the bare workspace endpoint's unfiltered query
  // (WorkspaceChats.forWorkspace) explicitly excludes any row with a non-null api_session_id -
  // "do not include api-session chats in the frontend for anyone," per that file's own comment.
  // Since chatRequestBody() always sends a sessionId (initializeAiConversation() guarantees
  // aiConfig.sessionId is set before any send is possible), every chat this integration sends is
  // unconditionally invisible to an unfiltered GET - apiSessionId isn't an optional narrowing
  // filter here, it's the only way this integration's own history is ever retrievable. Thread-
  // scoped GET .../chats genuinely has no query parameters at all (confirmed on the live OpenAPI
  // spec), so keep this conditional on !config.threadSlug.
  getHistory(config: ChatAiConfig): Observable<any> {
    const params = !config.threadSlug && config.sessionId ? { apiSessionId: config.sessionId } : {};
    return this.http.get(this.chatsUrl(config), { headers: this.authHeaders(config), params });
  }

  chat(config: ChatAiConfig, message: string, attachments?: MaisAttachment[]): Observable<MaisStreamChunk> {
    return this.http.post<MaisStreamChunk>(
      this.actionUrl(config, 'chat'),
      this.chatRequestBody(config, message, attachments),
      {
        headers: this.authHeaders(config)
      }
    );
  }

  uploadDocument(config: ChatAiConfig, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.workspaceUrl(config)}/document/upload`, formData, {
      headers: this.authHeaders(config)
    });
  }

  enhancePrompt(config: ChatAiConfig, message: string): Observable<any> {
    return this.http.post(
      `${this.workspaceUrl(config)}/enhance-prompt`,
      { message },
      { headers: this.authHeaders(config) }
    );
  }

  // Resolves once the stream closes normally; rejects on a non-2xx response or a chunk with
  // type:"abort" (native EventSource can't do POST, hence fetchEventSource). Each parsed chunk
  // is relayed on messagesChanged$ (a page-wide firehose) AND, if given, onChunk - callers that
  // need to know which chunks belong to their own call (chat.component.ts does, since
  // messagesChanged$ alone can't distinguish one caller's stream from another's) should use
  // onChunk rather than messagesChanged$.
  streamChat(
    config: ChatAiConfig,
    message: string,
    attachments?: MaisAttachment[],
    onChunk?: (chunk: MaisStreamChunk) => void
  ): Promise<void> {
    return fetchEventSource(this.actionUrl(config, 'stream-chat'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeaders(config)
      },
      body: JSON.stringify(this.chatRequestBody(config, message, attachments)),
      onopen: async response => {
        if (!response.ok) {
          throw new Error(`MAIS stream-chat request failed with status ${response.status}`);
        }
      },
      onmessage: event => {
        // Confirmed directly against a live server (not just the documented schema, which is
        // wrong for this endpoint): stream-chat's SSE events use `uuid`, not `id`, and the final
        // event is `type: "finalizeResponseStream"` (still carries `close: true`), not
        // `type: "textResponseChunk"` - only the non-streaming chat() response actually matches
        // the documented `id`/`textResponse` shape. Normalized here, at the parse boundary, so
        // every id-keyed accumulation/dedupe downstream (chat.component.ts) doesn't need to know
        // stream-chat and chat() disagree on the field name.
        const raw: MaisStreamChunk & { uuid?: string } = JSON.parse(event.data);
        const chunk: MaisStreamChunk = { ...raw, id: raw.id ?? raw.uuid };
        if (chunk.type === 'abort') {
          throw new Error(chunk.error || 'MAIS stream-chat aborted');
        }
        this.messagesChanged$.next(chunk);
        onChunk?.(chunk);
      },
      onerror: err => {
        throw err; // rethrow so fetchEventSource rejects instead of retrying indefinitely
      }
    });
  }

  private chatRequestBody(config: ChatAiConfig, message: string, attachments?: MaisAttachment[]) {
    const body: { message: string; mode?: string; sessionId?: string; attachments?: MaisAttachment[] } = {
      message,
      mode: config.mode
    };
    if (config.sessionId) {
      body.sessionId = config.sessionId;
    }
    if (attachments?.length) {
      body.attachments = attachments;
    }
    return body;
  }

  private actionUrl(config: ChatAiConfig, action: 'chat' | 'stream-chat'): string {
    return config.threadSlug
      ? `${this.workspaceUrl(config)}/thread/${config.threadSlug}/${action}`
      : `${this.workspaceUrl(config)}/${action}`;
  }

  private chatsUrl(config: ChatAiConfig): string {
    return config.threadSlug
      ? `${this.workspaceUrl(config)}/thread/${config.threadSlug}/chats`
      : `${this.workspaceUrl(config)}/chats`;
  }

  private workspaceUrl(config: ChatAiConfig): string {
    const base = (config.baseUrl ?? this.defaultBaseUrl ?? '').replace(/\/$/, '');
    return `${base}/v1/workspace/${config.workspaceSlug}`;
  }

  private authHeaders(config: ChatAiConfig): { Authorization: string } {
    return { Authorization: `Bearer ${config.authToken}` };
  }
}
